/* ============================================================
   INNOR — core : smooth scroll, navigation, AJAX cart
   ============================================================ */
(function () {
  'use strict';

  const INNOR = (window.INNOR = window.INNOR || {});

  /* ---------- Lenis + GSAP ---------- */
  INNOR.initScroll = function () {
    if (INNOR.lenis || typeof Lenis === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    const lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false
    });
    INNOR.lenis = lenis;

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  };

  /* ---------- Utils ---------- */
  INNOR.debounce = function (fn, delay) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  INNOR.formatMoney = function (cents) {
    /* Décimales seulement quand elles existent : « 80 € » et non « 80,00 € »,
       mais « 310,50 € » reste intact. Même règle que le filtre Liquid
       money_without_trailing_zeros, sinon un prix rendu par le serveur et le
       même prix recalculé en JS se contrediraient à l'écran. */
    const v = cents / 100;
    const amount = v % 1 === 0 ? String(v) : v.toFixed(2).replace('.', ',');
    return amount + ' €';
  };

  INNOR.lockScroll = function (locked) {
    if (INNOR.lenis) locked ? INNOR.lenis.stop() : INNOR.lenis.start();
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  };

  /* ---------- AJAX Cart ---------- */
  const cart = (INNOR.cart = {
    state: null,

    async fetch() {
      const res = await fetch('/cart.js');
      cart.state = await res.json();
      cart.emit();
      return cart.state;
    },

    /* `properties` porte les propriétés de ligne (précommande : délai annoncé
       et horodatage du consentement). Elles doivent voyager avec la LIGNE,
       pas avec le panier : un panier peut mêler une précommande et une pièce
       en stock, et c'est la ligne qui doit dire laquelle attend. */
    async add(variantId, quantity = 1, properties = null) {
      const payload = { id: variantId, quantity };
      if (properties && Object.keys(properties).length) payload.properties = properties;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Ajout au panier impossible');
      }
      return cart.fetch();
    },

    /* Ajout groupé — un seul appel pour toutes les lignes d'un ensemble.
       Enchaîner des add() ferait autant d'allers-retours que de pièces, avec
       le risque qu'une pièce épuisée en cours de route laisse un panier à
       moitié constitué. /cart/add.js accepte un tableau `items` et traite le
       lot de façon atomique : tout passe, ou rien. */
    async addMany(items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Ajout au panier impossible');
      }
      return cart.fetch();
    },

    async change(lineKey, quantity) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      return cart.fetch();
    },

    emit() {
      document.dispatchEvent(new CustomEvent('innor:cart', { detail: cart.state }));
    }
  });

  /* ---------- Taille modifiable dans le tiroir (18/10/2026) ----------
     La fiche coche M d'office : le visiteur ajuste sa taille ici. Le libellé de
     taille d'une ligne devient une liste des tailles du produit, lue sur
     /products/<handle>.js (une fois par produit). Un produit sans taille (le
     pendentif) garde son libellé tel quel. */
  const variantesParProduit = {};
  function variantesDe(handle) {
    if (!variantesParProduit[handle]) {
      variantesParProduit[handle] = fetch('/products/' + handle + '.js')
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => (p && p.variants && p.variants.length > 1 ? p.variants : null))
        .catch(() => null);
    }
    return variantesParProduit[handle];
  }
  function habillerTailles(state) {
    (state.items || []).forEach((item) => {
      if (!item.handle || !item.variant_title || item.variant_title === 'Default Title') return;
      variantesDe(item.handle).then((variantes) => {
        if (!variantes) return;
        const ligne = document.querySelector('.cart-line[data-line-key="' + item.key + '"] .cart-line__meta');
        if (!ligne || ligne.querySelector('select')) return;
        const select = document.createElement('select');
        select.className = 'cart-line__taille';
        select.setAttribute('data-line-size', '');
        select.setAttribute('aria-label', 'Taille');
        variantes.forEach((v) => {
          const o = document.createElement('option');
          o.value = v.id;
          o.textContent = v.title + (v.available ? '' : ' (épuisé)');
          o.disabled = !v.available && v.id !== item.variant_id;
          o.selected = v.id === item.variant_id;
          select.appendChild(o);
        });
        ligne.textContent = 'Taille ';
        ligne.appendChild(select);
      });
    });
  }
  /* Changer de taille = ajouter la nouvelle variante (mêmes propriétés de ligne,
     même quantité) puis retirer l'ancienne ; l'ajout d'abord, pour ne jamais
     vider le panier si la nouvelle taille est refusée. */
  document.addEventListener('change', async (e) => {
    const select = e.target.closest && e.target.closest('[data-line-size]');
    if (!select) return;
    const ligne = select.closest('.cart-line');
    const item = ((cart.state && cart.state.items) || []).find((i) => i.key === ligne.dataset.lineKey);
    const nouvelle = parseInt(select.value, 10);
    if (!item || !nouvelle || nouvelle === item.variant_id) return;
    select.disabled = true;
    try {
      await cart.add(nouvelle, item.quantity, item.properties);
      await cart.change(item.key, 0);
    } catch (err) {
      await cart.fetch();
    }
  });

  /* ---------- Rendu du panneau panier ---------- */
  function renderCartPanel(state) {
    /* On écrit dans [data-cart-lines], PAS dans le corps entier : le corps
       porte aussi le bandeau de promesse et la carte de vente additionnelle
       (innor-panier.js). Écraser son innerHTML les effaçait à chaque
       rafraîchissement du panier, c'est-à-dire à chaque ajout — soit
       exactement quand la carte venait d'être rendue. Repli sur le corps pour
       un balisage ancien qui n'aurait pas encore le conteneur. */
    const body = document.querySelector('[data-cart-lines]') || document.querySelector('[data-cart-body]');
    const foot = document.querySelector('[data-cart-foot]');
    const counts = document.querySelectorAll('[data-cart-count]');
    if (!state) return;

    counts.forEach((el) => { el.textContent = String(state.item_count).padStart(2, '0'); });

    if (!body) return;

    /* Le consentement suit le pied : sous un panier vide, une mention de
       précommande ne s'adresse à personne. */
    const consent = document.querySelector('[data-cart-consent]');

    if (state.item_count === 0) {
      body.innerHTML = '<p class="t-meta" style="opacity:.6">Le panier est vide.</p>';
      if (foot) foot.hidden = true;
      if (consent) consent.hidden = true;
      return;
    }
    if (foot) foot.hidden = false;
    /* La jupe est en stock, pas en précommande : un panier qui ne contient
       qu'elle n'a pas de consentement à donner. Dès qu'une autre pièce s'y
       ajoute, la case revient (innor-preorder.js ignore une case masquée). */
    /* 01/11/2026 : le pantalon part désormais sous 2 à 4 semaines (dit par le
       client, déjà affiché à la caisse) : lui non plus n'a plus de précommande
       à accepter. Même chose pour le pack pantalon + jupe et le pendentif
       offert qui l'accompagne. Veste, pack veste + jupe : la case reste. */
    const SANS_PRECOMMANDE = ['denim-skirt-ronin', 'denim-pant-ronin', 'pack-ronin-bf', 'signature'];
    const seulementJupe = state.items.every((i) => SANS_PRECOMMANDE.indexOf(i.handle) !== -1);
    if (consent) consent.hidden = seulementJupe;

    body.innerHTML = state.items
      .map(
        (item) => `
      <div class="cart-line" data-line-key="${item.key}">
        <div class="cart-line__img">${item.image ? `<img src="${item.image.replace(/(\.[a-z]+)(\?|$)/, '_200x$1$2')}" alt="" loading="lazy">` : ''}</div>
        <div>
          <p class="cart-line__title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-line__meta">${item.variant_title}</p>` : ''}
          <div class="cart-line__qty">
            <button type="button" data-qty-minus aria-label="Réduire la quantité">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-qty-plus aria-label="Augmenter la quantité">+</button>
          </div>
        </div>
        <div style="text-align:right; display:grid; gap:.6rem; align-content:start">
          <span class="cart-line__price">${INNOR.formatMoney(item.final_line_price)}</span>
          <button type="button" class="cart-line__remove" data-line-remove>Retirer</button>
        </div>
      </div>`
      )
      .join('');
    habillerTailles(state);

    /* Même correction que sur la page panier : items_subtotal_price ignore
       les remises de COMMANDE, donc le panneau pouvait annoncer plus cher que
       la caisse. total_price est le seul montant qui comprend tout.
       On nomme aussi la remise obtenue — un prix qui baisse sans explication
       inquiète autant qu'un prix qui monte. */
    const remisesEl = document.querySelector('[data-cart-remises]');
    if (remisesEl) {
      /* LES REMISES VIENNENT DE DEUX ENDROITS, PAS D'UN.

         `cart_level_discount_applications` ne porte que les remises de
         COMMANDE. Une règle « achetez 2 pièces, obtenez le pendentif offert »
         est une remise PRODUIT : Shopify l'attribue à la ligne, dans
         `line_level_discount_allocations`, et le tableau de commande reste
         vide. Vérifié sur le panier réel le 25/08/2026 — la remise s'appelle
         « Pendentif Signature », vaut 8 000 centimes sur la ligne du
         pendentif, et n'apparaissait nulle part dans le tiroir : le total
         baissait de 80 € sans que rien ne dise pourquoi.

         Une même remise est allouée à PLUSIEURS lignes, la plupart pour un
         montant nul. On regroupe donc par clé et on écarte les montants à
         zéro, sinon la même promesse s'écrirait trois fois dont deux à
         « −0 € ». */
      const parCle = {};
      (state.items || []).forEach((item) => {
        (item.line_level_discount_allocations || []).forEach((a) => {
          if (!a.amount) return;
          const app = a.discount_application || {};
          const cle = app.key || app.title || 'remise';
          if (!parCle[cle]) parCle[cle] = { title: app.title || 'Remise', total_allocated_amount: 0 };
          parCle[cle].total_allocated_amount += a.amount;
        });
      });
      const remises = (state.cart_level_discount_applications || [])
        .concat(Object.keys(parCle).map((k) => parCle[k]));

      /* LA VALEUR AVANT RÉDUCTION, EN TÊTE DU PIED.

         Sans elle, le panier n'affichait qu'un sous-total : le pendentif à
         0 € et les remises se lisaient comme un prix bas, pas comme un
         avantage obtenu. La ligne barrée redonne l'échelle — c'est la somme
         qu'aurait coûté le même panier sans rien.

         `original_total_price` est le montant AVANT toute remise, cadeau
         compris à son prix. On ne l'affiche que s'il diffère du total :
         au-dessus d'un panier sans avantage, une valeur barrée identique au
         sous-total ferait une fausse promesse. */
      const brut = state.original_total_price || 0;
      const economie = brut - state.total_price;
      const valeur = economie > 0
        ? `<div class="cart-valeur"><span class="t-meta">Valeur sans réduction</span>
            <s>${INNOR.formatMoney(brut)}</s></div>`
        : '';

      remisesEl.innerHTML = valeur + remises
        .map(
          (r) => `<div class="cart-remise"><span class="t-meta">${r.title}</span>
            <span>−${INNOR.formatMoney(r.total_allocated_amount)}</span></div>`
        )
        .join('');
      remisesEl.hidden = remises.length === 0 && !valeur;
    }

    const totalEl = document.querySelector('[data-cart-total]');
    if (totalEl) totalEl.textContent = INNOR.formatMoney(state.total_price);

    /* La livraison, avant la caisse. Le tarif et le seuil viennent du
       balisage (cart-panel.liquid), qui reprend ceux de l'Admin. Seuls les
       pays listés dans data-zones sont chiffrés : ailleurs on ne promet rien.
       Le seuil se compare à total_price (après remises), comme la caisse. */
    const livEl = document.querySelector('[data-cart-livraison]');
    if (livEl) {
      const zones = (livEl.dataset.zones || '').split(',');
      const prix = parseInt(livEl.dataset.prix, 10);
      const seuil = parseInt(livEl.dataset.seuil, 10);
      const label = '<span class="t-meta">Livraison</span>';
      /* 01/11/2026 (dit par le client) : sous le seuil la livraison est
         payante, et on n'affiche alors AUCUNE ligne : le tiroir ne parle
         de livraison que pour dire qu'elle est offerte. Dès le seuil
         atteint, « Offerte » avec le tarif barré. Hors zones connues, le
         thème ne promet rien : « Calculée au paiement ». */
      let visible = true;
      if (zones.indexOf(livEl.dataset.pays) === -1 || !prix || !seuil) {
        livEl.innerHTML = label + '<span class="cart-livraison__note">Calculée au paiement</span>';
      } else if (state.total_price >= seuil) {
        livEl.innerHTML = label + '<span><s>' + INNOR.formatMoney(prix) + '</s> <strong>Offerte</strong></span>';
      } else {
        livEl.innerHTML = '';
        visible = false;
      }
      livEl.hidden = !visible;
    }

    /* Le contenu vient de changer de hauteur : on redit s'il reste de la
       commande sous le pli. Après le prochain rendu, sinon on mesure encore
       l'ancien contenu. */
    requestAnimationFrame(marquerDefilement);
  }

  /* Y a-t-il encore quelque chose sous le bord bas de la zone défilante ?
     La classe pilote l'ombre posée au-dessus du pied (innor-base.css). On
     mesure une distance au bas plutôt qu'un simple `scrollHeight >
     clientHeight` : arrivé en bas, il n'y a plus rien à annoncer, et une
     ombre qui reste allumée en fin de liste ment autant qu'une absente. */
  function marquerDefilement() {
    const panel = document.querySelector('[data-cart-panel]');
    if (!panel) return;
    /* Qui défile dépend de la hauteur de l'écran : le corps seul en temps
       normal, le panneau entier sous 700 px de haut (voir innor-base.css).
       On interroge donc le DOM au lieu de le supposer. */
    const body = document.querySelector('[data-cart-body]');
    const zone = panel.scrollHeight > panel.clientHeight + 1 ? panel : body;
    if (!zone) return;
    const reste = zone.scrollHeight - zone.clientHeight - zone.scrollTop;
    panel.classList.toggle('a-defiler', reste > 8);
  }

  /* ---------- Panneaux latéraux génériques ---------- */
  const panels = (INNOR.panels = {
    open(sel) {
      const panel = document.querySelector(sel);
      const backdrop = document.querySelector('[data-panel-backdrop]');
      if (!panel) return;
      panels.closeAll();
      panel.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-active');
      INNOR.lockScroll(true);
      const focusable = panel.querySelector('button, a, input');
      if (focusable) focusable.focus({ preventScroll: true });
    },
    closeAll() {
      document.querySelectorAll('.side-panel.is-open').forEach((p) => p.classList.remove('is-open'));
      const backdrop = document.querySelector('[data-panel-backdrop]');
      if (backdrop) backdrop.classList.remove('is-active');
      INNOR.lockScroll(false);
    }
  });

  /* ---------- Bindings ---------- */
  function bind() {
    // Burger
    const burger = document.querySelector('[data-burger]');
    const overlay = document.querySelector('[data-nav-overlay]');
    if (burger && overlay) {
      burger.addEventListener('click', () => {
        const open = overlay.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', String(open));
        INNOR.lockScroll(open);
      });
      overlay.querySelectorAll('a').forEach((a) =>
        a.addEventListener('click', () => {
          overlay.classList.remove('is-open');
          burger.setAttribute('aria-expanded', 'false');
          INNOR.lockScroll(false);
        })
      );
    }

    // Ouverture panier
    document.addEventListener('click', (e) => {
      const openCart = e.target.closest('[data-open-cart]');
      if (openCart) {
        e.preventDefault();
        panels.open('[data-cart-panel]');
      }
      if (e.target.closest('[data-panel-close]') || e.target.closest('[data-panel-backdrop]')) {
        panels.closeAll();
      }

      // Quantités / suppression dans le panier
      const line = e.target.closest('.cart-line');
      if (line) {
        const key = line.dataset.lineKey;
        const qty = parseInt(line.querySelector('.cart-line__qty span')?.textContent || '1', 10);
        if (e.target.closest('[data-qty-minus]')) cart.change(key, Math.max(0, qty - 1));
        if (e.target.closest('[data-qty-plus]')) cart.change(key, qty + 1);
        if (e.target.closest('[data-line-remove]')) cart.change(key, 0);
      }

    });

    /* Coche « Offre Duo » (fiche pendentif, hors du <form> pour se poser plus
       haut dans la colonne d'achat) : cochée, elle pose la quantité du
       formulaire à 2 et affiche le total du duo sur le bouton d'achat ;
       décochée, elle repose tout à l'état simple. Un seul geste d'achat
       (le bouton « Ajouter au panier » normal) au lieu d'un second bouton
       séparé — voir le commentaire du bloc duo_offer dans main-product.liquid.
       'change', pas 'click' : une case à cocher se pilote aussi au clavier. */
    document.addEventListener('change', (e) => {
      const duoCheck = e.target.closest('[data-duo-check]');
      if (!duoCheck) return;
      const targetForm = document.getElementById(duoCheck.getAttribute('data-duo-check'));
      if (!targetForm) return;
      const qtyField = targetForm.querySelector('[data-buy-quantity]');
      if (qtyField) qtyField.value = duoCheck.checked ? '2' : '1';
      const priceEl = targetForm.querySelector('[data-buy-total-price]');
      if (priceEl) {
        const base = parseInt(duoCheck.dataset.basePrice, 10) || 0;
        const duo = parseInt(duoCheck.dataset.duoPrice, 10) || base;
        priceEl.textContent = INNOR.formatMoney(duoCheck.checked ? duo : base);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') panels.closeAll();
    });

    /* Fiches produit : ajout AJAX sans rechargement (PDP standard).

       UN SEUL AddToCart PAR AJOUT (30/09/2026). Le suivi natif de Shopify
       écoute l'événement `submit` des formulaires produit ET l'appel à
       /cart/add.js : chaque ajout partait donc deux fois vers Meta et TikTok,
       et la version « submit » lisait le premier champ id du formulaire (XS)
       au lieu de la taille cochée. Le clic sur un bouton d'ajout est donc
       intercepté AVANT que le navigateur ne déclenche `submit` : seul l'appel
       à /cart/add.js reste, avec la bonne variante. L'écouteur `submit`
       demeure pour la touche Entrée. */
    document.addEventListener('click', (e) => {
      const btn = e.target.closest ? e.target.closest('button[type="submit"], input[type="submit"]') : null;
      if (!btn || btn.disabled) return;
      const form = btn.form;
      if (!form || !form.matches('[data-ajax-cart-form]')) return;
      e.preventDefault();
      ajouter(form, btn);
    }, true);

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-ajax-cart-form]');
      if (!form) return;
      e.preventDefault();
      ajouter(form, null);
    });

    /* 18/10/2026 : un seul ajout à la fois par formulaire. Le bouton collant
       n'est pas celui qui était désactivé pendant l'appel (c'était le premier
       bouton du formulaire, hors écran) : un second appui partait donc en
       double. Le retour visuel (« Ajout… ») se pose maintenant sur le bouton
       réellement touché. */
    async function ajouter(form, clique) {
      if (form.dataset.ajoutEnCours) return;
      form.dataset.ajoutEnCours = '1';
      const data = new FormData(form);
      const id = parseInt(data.get('id'), 10);
      /* Aucune taille n'est présélectionnée depuis le 30/08 : un clic sur
         « Ajouter au panier » sans rien avoir choisi n'a plus de repli. Sans
         ce garde, Shopify recevait un id NaN et renvoyait une erreur brute
         d'API à la place d'un message compréhensible. */
      if (!id) {
        const errorEl = form.querySelector('[data-form-error]');
        if (errorEl) errorEl.textContent = 'Choisissez une taille.';
        delete form.dataset.ajoutEnCours;
        return;
      }
      /* Tout champ nommé properties[…] du formulaire devient une propriété de
         ligne — c'est la convention Shopify, on la respecte plutôt que de
         câbler chaque champ un par un. Les valeurs vides sont écartées :
         Shopify afficherait sinon une propriété sans contenu sur la commande. */
      const properties = {};
      data.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== '') properties[match[1]] = value;
      });
      /* Quantité normalement 1. La coche « Offre Duo » (fiche pendentif, hors
         de ce <form>) la pose à 2 quand elle est cochée — lue ici telle
         quelle, JAMAIS remise à 1 d'office : cette valeur reflète un état
         visible (la coche), pas un geste ponctuel. Si l'ajout échoue (case
         de précommande non cochée), la coche et la quantité doivent rester
         telles que le client les a laissées. Elles ne repassent à l'état
         simple qu'après un ajout RÉUSSI, plus bas. */
      const quantity = parseInt(data.get('quantity'), 10) || 1;
      const btn = clique || form.querySelector('[type="submit"]');
      const errorEl = form.querySelector('[data-form-error]');
      /* innerHTML, jamais textContent : ce bouton peut contenir deux <span>
         (libellé + montant, sur la fiche pendentif) que textContent aurait
         détruits en les aplatissant en un seul nœud de texte — la coche
         Duo n'aurait alors plus rien à mettre à jour au prochain clic. */
      const label = btn ? btn.innerHTML : '';
      if (errorEl) errorEl.textContent = '';
      if (btn) { btn.disabled = true; btn.innerHTML = 'Ajout…'; }
      try {
        await cart.add(id, quantity, properties);
        /* Un ajout réussi ouvre le panier — SAUF si quelqu'un d'autre a
           mieux à proposer à cet instant précis. innor-ensemble.js écoute cet
           événement pour glisser la fenêtre « il vous manque une pièce »
           quand le vêtement part seul (14 août) : c'est le seul moment où
           l'upsell a encore une chance, et il ne peut pas cohabiter avec le
           panneau panier qui s'ouvrirait par-dessus.

           Événement annulable plutôt qu'un appel direct : le noyau ignore ce
           qu'est un ensemble, et une fiche sans carte d'ensemble continue
           d'ouvrir le panier comme avant, sans rien savoir de tout ça. */
        const added = new CustomEvent('innor:added', {
          cancelable: true,
          detail: { form, variantId: id, properties }
        });
        const handled = !document.dispatchEvent(added);
        if (!handled) panels.open('[data-cart-panel]');
        if (btn) { btn.innerHTML = label; btn.disabled = false; }
        delete form.dataset.ajoutEnCours;
        /* La coche redescend à l'état simple APRÈS avoir restauré le
           libellé : sinon le 'change' qu'elle déclenche mettrait à jour un
           <span> que btn.innerHTML = label vient de recréer identique mais
           un instant plus tard, et on perdrait la mise à jour. */
        const duoCheck = document.querySelector('[data-duo-check="' + form.id + '"]');
        if (duoCheck && duoCheck.checked) {
          duoCheck.checked = false;
          duoCheck.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (err) {
        if (errorEl) errorEl.textContent = err.message;
        if (btn) { btn.innerHTML = label; btn.disabled = false; }
        delete form.dataset.ajoutEnCours;
      }
    }

    document.addEventListener('innor:cart', (e) => renderCartPanel(e.detail));

    const corps = document.querySelector('[data-cart-body]');
    const panneau = document.querySelector('[data-cart-panel]');
    if (panneau) panneau.addEventListener('scroll', marquerDefilement, { passive: true });
    if (corps) {
      corps.addEventListener('scroll', marquerDefilement, { passive: true });
      /* Une image de ligne qui finit de charger change la hauteur du contenu
         sans qu'aucun événement de panier ne se produise. */
      if (window.ResizeObserver) new ResizeObserver(marquerDefilement).observe(corps);
    }

    cart.fetch();

    /* Retour depuis /cart. Cette page n'est plus qu'un repli sans JavaScript :
       quand le script tourne, elle renvoie d'où l'on vient en posant ce
       drapeau, et c'est ici qu'on rouvre le tiroir. Le drapeau est consommé
       aussitôt — un rechargement ne doit pas rouvrir le panier une deuxième
       fois. */
    try {
      if (sessionStorage.getItem('innor:ouvrir-panier')) {
        sessionStorage.removeItem('innor:ouvrir-panier');
        panels.open('[data-cart-panel]');
      }
    } catch (e) {
      /* Navigation privée : sessionStorage lève. Rien à rattraper, on n'ouvre
         simplement pas le tiroir. */
    }
  }

  /* ---------- Voile de lisibilité de la barre ----------
     La barre est fixe et se blende avec ce qu'elle survole (voir
     innor-base.css). Sur un aplat c'est parfait ; sur une photo l'inversion
     part en cyan et le wordmark se coupe en deux couleurs à la frontière de
     l'image. On pose donc un voile, mais SEULEMENT quand une photo est
     réellement passée sous la barre.

     Le déclencheur est une position à l'écran, pas une page : rootMargin
     rétrécit la zone d'observation à la bande occupée par la barre, en haut du
     viewport. Un élément « entre » donc dans cette bande exactement quand il
     commence à passer dessous — ce que ni le scroll ni un test de page ne
     sauraient dire aussi simplement.

     Compté plutôt que basculé sur un booléen : plusieurs photos peuvent
     traverser la bande en même temps (rail du carrousel), et un simple
     `isIntersecting` du dernier événement éteindrait le voile dès que l'UNE
     d'elles sort, alors qu'une autre est encore dessous. */
  function initVoileNav() {
    const nav = document.querySelector('.site-nav');
    if (!nav || !('IntersectionObserver' in window)) return;
    const cibles = document.querySelectorAll('[data-nav-voile], .carrousel__media');
    if (!cibles.length) return;

    const hauteur = Math.round(nav.getBoundingClientRect().height) || 56;
    const dessous = new Set();

    const obs = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((e) => {
          if (e.isIntersecting) dessous.add(e.target);
          else dessous.delete(e.target);
        });
        nav.classList.toggle('site-nav--voile', dessous.size > 0);
      },
      { rootMargin: `0px 0px -${Math.max(0, window.innerHeight - hauteur)}px 0px`, threshold: 0 }
    );
    cibles.forEach((c) => obs.observe(c));
  }

  /* ---------- Bandeau supérieur : compte à rebours ---------- */
  INNOR.initAnnouncementBar = function () {
    /* Défilement piloté par la MESURE, pas par le nombre de messages
       (20/09/2026). Un message unique qui tient à l'écran reste fixe ; dès
       qu'il déborde — un mobile étroit, un stock qui passe à deux chiffres,
       une rotation d'écran — le rail reprend son défilement droite→gauche.
       Le groupe est en `flex-shrink: 0` et `white-space: nowrap`, donc sa
       largeur naturelle se mesure pareil dans les deux états : pas besoin
       de retirer la classe pour mesurer. */
    const bar = document.querySelector('[data-announcement-bar]');
    const groupe = bar && bar.querySelector('.announcement-bar__group');
    if (bar && groupe) {
      const ajuster = function () {
        bar.classList.toggle(
          'announcement-bar--statique',
          groupe.scrollWidth <= bar.clientWidth
        );
      };
      ajuster();
      /* La typo variable arrive après le premier rendu et change la largeur
         du texte : remesurer une fois qu'elle est posée, sinon la décision
         est prise sur la police de repli. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(ajuster).catch(function () {});
      }
      window.addEventListener('resize', ajuster);
      window.addEventListener('orientationchange', ajuster);
    }

    /* Le doublon du rail est masqué aux lecteurs d'écran : ses boutons ne
       doivent pas non plus prendre le focus clavier. Un clic sur le message
       ouvre le popup livraison (écran de l'adresse). */
    document.querySelectorAll('.announcement-bar__group[aria-hidden="true"] button').forEach((b) => { b.tabIndex = -1; });
    document.querySelectorAll('[data-pop-ouvrir]').forEach((b) => {
      b.addEventListener('click', () => window.dispatchEvent(new CustomEvent('innor:ouvrir-port')));
    });

    const nodes = document.querySelectorAll('[data-countdown]');
    if (!nodes.length) return;

    function render() {
      nodes.forEach((node) => {
        const target = new Date(node.dataset.target).getTime();
        if (isNaN(target)) return;
        const diff = target - Date.now();
        if (diff <= 0) {
          /* Fin de l'offre : le message disparaît (04/10/2026), un « terminé »
             resterait à l'écran comme une promesse échue. */
          const item = node.closest('.announcement-bar__item');
          if (item) item.hidden = true;
          return;
        }
        const totalMinutes = Math.floor(diff / 60000);
        const days = Math.floor(totalMinutes / 1440);
        const hours = Math.floor((totalMinutes % 1440) / 60);
        const minutes = totalMinutes % 60;
        /* Format francisé le 20/09/2026 : c'était `3D 8H 12M` et `ended`,
           de l'anglais sur un site français. `J` pour jours. Le libellé qui
           précède est devenu un réglage de la section. */
        /* Moins de 72 h : heures, minutes, secondes (04/10/2026, demande
           client, plus visuel pour une offre de 48 h). Au-delà, l'ancien
           format en jours. */
        const sec = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
        node.textContent = diff < 72 * 3600000
          ? `${Math.floor(totalMinutes / 60)}H ${String(minutes).padStart(2, '0')}M ${sec}S`
          : `${days}J ${hours}H ${minutes}M`;
      });
    }

    render();
    setInterval(render, 1000);
  };

  document.addEventListener('DOMContentLoaded', () => {
    INNOR.initScroll();
    bind();
    initVoileNav();
    INNOR.initAnnouncementBar();
  });
})();

/* 18/10/2026 (demande client, comme la fiche Arntreal) : le bandeau supérieur ne
   reste pas au défilement. Dès qu'on descend, il remonte hors de l'écran et la
   barre de navigation prend sa place en haut ; il revient quand on remonte tout
   en haut de la page. Simple classe sur <html>, le reste est en CSS. */
(function () {
  var racine = document.documentElement, attente = false;
  function maj() {
    attente = false;
    racine.classList.toggle('annonce-cachee', window.scrollY > 8);
  }
  window.addEventListener('scroll', function () {
    if (!attente) { attente = true; requestAnimationFrame(maj); }
  }, { passive: true });
  maj();
})();
